//frontend/src/admin/ContactManagement.js
import React, { useState, useEffect } from 'react';
import {
  FaPhone, FaPlus, FaEdit, FaTrash, FaSave, FaTimes,
  FaToggleOn, FaToggleOff, FaArrowUp, FaArrowDown
} from 'react-icons/fa';
import axiosInstance from '../axios';
import '../styles/admin.css';

const ContactManagement = () => {
  const [activeTab, setActiveTab] = useState('cta'); // 'cta', 'contact', эсвэл 'footer'
  
  // CTA Settings
  const [ctaSettings, setCtaSettings] = useState([]);
  const [showCtaModal, setShowCtaModal] = useState(false);
  const [editingCta, setEditingCta] = useState(null);
  const [ctaForm, setCtaForm] = useState({
    section_title: '',
    section_description: '',
    phone_numbers: []
  });
  const [newPhone, setNewPhone] = useState({ label: '', number: '', flag: '📞' });
  
  // Contact Info
  const [contactInfo, setContactInfo] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    section_title: '',
    contact_items: [],
    display_order: 0,
    is_active: true
  });
  const [newContactItem, setNewContactItem] = useState('');

  // ✅ FOOTER CONTACT - ШИНЭ ХЭСЭГ
  const [footerContacts, setFooterContacts] = useState({
    korea_phone_1: '',
    korea_phone_2: '',
    korea_phone_3: '',
    mongolia_phone_1: '',
    mongolia_phone_2: '',
    mongolia_phone_3: '',
    email: ''
  });
  const [footerLoading, setFooterLoading] = useState(false);
  const [footerMessage, setFooterMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'footer') {
      fetchFooterContacts();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [ctaRes, contactRes] = await Promise.all([
        axiosInstance.get('/api/admin/cta-settings'),
        axiosInstance.get('/api/admin/contact-info')
      ]);
      
      if (ctaRes.data.success) setCtaSettings(ctaRes.data.data);
      if (contactRes.data.success) setContactInfo(contactRes.data.data);
    } catch (error) {
      console.error('Fetch data error:', error);
      alert('Өгөгдөл татахад алдаа гарлаа');
    }
  };

  // ==================== FOOTER CONTACT FUNCTIONS ====================
  
  const fetchFooterContacts = async () => {
    try {
      setFooterLoading(true);
      const response = await axiosInstance.get('/api/footer-contacts');
      
      if (response.data.success) {
        const contacts = {};
        response.data.data.forEach(item => {
          const key = item.setting_key.replace('footer_contact_', '');
          contacts[key] = item.setting_value || '';
        });
        setFooterContacts(prev => ({ ...prev, ...contacts }));
      }
    } catch (error) {
      console.error('Footer contacts fetch error:', error);
      showFooterMessage('error', 'Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setFooterLoading(false);
    }
  };

  const handleFooterContactChange = (e) => {
    const { name, value } = e.target;
    setFooterContacts(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFooterContactSave = async (e) => {
    e.preventDefault();
    
    try {
      setFooterLoading(true);
      const response = await axiosInstance.put('/api/footer-contacts', {
        contacts: footerContacts
      });
      
      if (response.data.success) {
        showFooterMessage('success', '✅ Footer холбоо барих мэдээлэл амжилттай хадгалагдлаа');
      }
    } catch (error) {
      console.error('Footer contact save error:', error);
      showFooterMessage('error', '❌ ' + (error.response?.data?.msg || 'Хадгалахад алдаа гарлаа'));
    } finally {
      setFooterLoading(false);
    }
  };

  const showFooterMessage = (type, text) => {
    setFooterMessage({ type, text });
    setTimeout(() => setFooterMessage({ type: '', text: '' }), 3000);
  };

  // ==================== CTA SETTINGS ====================
  
  const openCtaModal = (cta = null) => {
    if (cta) {
      setEditingCta(cta);
      setCtaForm({
        section_title: cta.section_title,
        section_description: cta.section_description,
        phone_numbers: cta.phone_numbers || []
      });
    } else {
      setEditingCta(null);
      setCtaForm({
        section_title: '',
        section_description: '',
        phone_numbers: []
      });
    }
    setShowCtaModal(true);
  };

  const handleCtaSave = async () => {
    if (!ctaForm.section_title.trim()) {
      alert('Гарчиг оруулна уу');
      return;
    }

    try {
      if (editingCta) {
        await axiosInstance.put(`/api/admin/cta-settings/${editingCta.id}`, ctaForm);
        alert('✅ Тохиргоо шинэчлэгдлээ');
      } else {
        await axiosInstance.post('/api/admin/cta-settings', ctaForm);
        alert('✅ Тохиргоо нэмэгдлээ');
      }
      
      setShowCtaModal(false);
      fetchData();
    } catch (error) {
      console.error('Save CTA error:', error);
      alert('❌ ' + (error.response?.data?.msg || 'Алдаа гарлаа'));
    }
  };

  const handleCtaToggle = async (id) => {
    try {
      await axiosInstance.patch(`/api/admin/cta-settings/${id}/toggle`);
      alert('✅ Тохиргоо идэвхжлээ');
      fetchData();
    } catch (error) {
      console.error('Toggle CTA error:', error);
      alert('❌ Алдаа гарлаа');
    }
  };

  const addPhone = () => {
    if (!newPhone.number.trim()) {
      alert('Утасны дугаар оруулна уу');
      return;
    }
    
    setCtaForm({
      ...ctaForm,
      phone_numbers: [...ctaForm.phone_numbers, { ...newPhone }]
    });
    
    setNewPhone({ label: '', number: '', flag: '📞' });
  };

  const removePhone = (index) => {
    setCtaForm({
      ...ctaForm,
      phone_numbers: ctaForm.phone_numbers.filter((_, i) => i !== index)
    });
  };

  // ==================== CONTACT INFO ====================
  
  const openContactModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        section_title: contact.section_title,
        contact_items: contact.contact_items || [],
        display_order: contact.display_order,
        is_active: contact.is_active
      });
    } else {
      setEditingContact(null);
      setContactForm({
        section_title: '',
        contact_items: [],
        display_order: 0,
        is_active: true
      });
    }
    setShowContactModal(true);
  };

  const handleContactSave = async () => {
    if (!contactForm.section_title.trim()) {
      alert('Гарчиг оруулна уу');
      return;
    }

    if (contactForm.contact_items.length === 0) {
      alert('Холбоо барих мэдээлэл нэмнэ үү');
      return;
    }

    try {
      if (editingContact) {
        await axiosInstance.put(`/api/admin/contact-info/${editingContact.id}`, contactForm);
        alert('✅ Холбоо барих мэдээлэл шинэчлэгдлээ');
      } else {
        await axiosInstance.post('/api/admin/contact-info', contactForm);
        alert('✅ Холбоо барих мэдээлэл нэмэгдлээ');
      }
      
      setShowContactModal(false);
      fetchData();
    } catch (error) {
      console.error('Save contact error:', error);
      alert('❌ ' + (error.response?.data?.msg || 'Алдаа гарлаа'));
    }
  };

  const handleContactDelete = async (id) => {
    if (!window.confirm('⚠️ Энэ холбоо барих мэдээллийг устгах уу?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/admin/contact-info/${id}`);
      alert('✅ Устгагдлаа');
      fetchData();
    } catch (error) {
      console.error('Delete contact error:', error);
      alert('❌ Устгахад алдаа гарлаа');
    }
  };

  const addContactItem = () => {
    if (!newContactItem.trim()) {
      alert('Холбоо барих мэдээлэл оруулна уу');
      return;
    }
    
    setContactForm({
      ...contactForm,
      contact_items: [...contactForm.contact_items, newContactItem]
    });
    
    setNewContactItem('');
  };

  const removeContactItem = (index) => {
    setContactForm({
      ...contactForm,
      contact_items: contactForm.contact_items.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <FaPhone className="mr-3 text-blue-600" />
          Холбоо барих удирдлага
        </h1>
        <p className="text-sm text-gray-600">
          Client хуудасны холбоо барих мэдээллүүд
        </p>
      </div>

      {/* Tabs - ✅ FOOTER ТАБ НЭМСЭН */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('cta')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'cta'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            CTA Тохиргоо
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'contact'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            Холбоо барих хэсэг
          </button>
          <button
            onClick={() => setActiveTab('footer')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'footer'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            Холбоо барих (Footer)
          </button>
        </div>

        {/* TAB CONTENT */}
        <div className="p-6">
          {/* CTA TAB */}
          {activeTab === 'cta' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">CTA Settings</h2>
                <button
                  onClick={() => openCtaModal()}
                  className="custom-button flex items-center gap-2"
                >
                  <FaPlus />
                  Шинэ нэмэх
                </button>
              </div>

              {ctaSettings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  CTA тохиргоо байхгүй байна
                </div>
              ) : (
                <div className="space-y-4">
                  {ctaSettings.map((cta) => (
                    <div key={cta.id} className="custom-card">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-2">{cta.section_title}</h3>
                        <p className="text-gray-600 text-sm mb-3">{cta.section_description}</p>
                        
                        <div className="flex flex-wrap gap-2">
                          {cta.phone_numbers && cta.phone_numbers.map((phone, idx) => (
                            <span key={idx} className="badge badge-primary">
                              {phone.flag} {phone.label}: {phone.number}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4 md:mt-0">
                        <button
                          onClick={() => handleCtaToggle(cta.id)}
                          className="icon-button"
                          title={cta.is_active ? 'Идэвхгүй болгох' : 'Идэвхжүүлэх'}
                        >
                          {cta.is_active ? (
                            <FaToggleOn className="text-green-500 text-2xl" />
                          ) : (
                            <FaToggleOff className="text-gray-400 text-2xl" />
                          )}
                        </button>
                        <button
                          onClick={() => openCtaModal(cta)}
                          className="icon-button text-blue-600"
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CONTACT INFO TAB */}
          {activeTab === 'contact' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Холбоо барих хэсэг</h2>
                <button
                  onClick={() => openContactModal()}
                  className="custom-button flex items-center gap-2"
                >
                  <FaPlus />
                  Шинэ нэмэх
                </button>
              </div>

              {contactInfo.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Холбоо барих мэдээлэл байхгүй байна
                </div>
              ) : (
                <div className="space-y-4">
                  {contactInfo.map((contact) => (
                    <div key={contact.id} className="custom-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-bold text-lg">{contact.section_title}</h3>
                          <span className={`badge ${contact.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {contact.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                          </span>
                          <span className="badge badge-primary">
                            Дараалал: {contact.display_order}
                          </span>
                        </div>
                        
                        <ul className="space-y-1 text-sm text-gray-700">
                          {contact.contact_items && contact.contact_items.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <span className="text-blue-600">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex gap-2 mt-4 md:mt-0">
                        <button
                          onClick={() => openContactModal(contact)}
                          className="icon-button text-blue-600"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleContactDelete(contact.id)}
                          className="icon-button text-red-600"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ✅ FOOTER CONTACT TAB - ШИНЭ ХЭСЭГ */}
          {activeTab === 'footer' && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Холбоо барих (Footer)</h2>
              
              {footerMessage.text && (
                <div className={`p-4 rounded-lg mb-6 ${
                  footerMessage.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {footerMessage.text}
                </div>
              )}

              <form onSubmit={handleFooterContactSave} className="space-y-6">
                {/* СОЛОНГОСЫН ХОЛБОО БАРИХ */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-200">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    🇰🇷 Солонгосын холбоо барих
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 1:
                      </label>
                      <input
                        type="text"
                        name="korea_phone_1"
                        value={footerContacts.korea_phone_1}
                        onChange={handleFooterContactChange}
                        placeholder="070-8018-8678"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 2:
                      </label>
                      <input
                        type="text"
                        name="korea_phone_2"
                        value={footerContacts.korea_phone_2}
                        onChange={handleFooterContactChange}
                        placeholder="010-5815-3005"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 3:
                      </label>
                      <input
                        type="text"
                        name="korea_phone_3"
                        value={footerContacts.korea_phone_3}
                        onChange={handleFooterContactChange}
                        placeholder="010-8088-2005"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>
                  </div>
                </div>

                {/* МОНГОЛЫН ХОЛБОО БАРИХ */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-200">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    🇲🇳 Монголын холбоо барих
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 1:
                      </label>
                      <input
                        type="text"
                        name="mongolia_phone_1"
                        value={footerContacts.mongolia_phone_1}
                        onChange={handleFooterContactChange}
                        placeholder="(+976) 7786-8678"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 2:
                      </label>
                      <input
                        type="text"
                        name="mongolia_phone_2"
                        value={footerContacts.mongolia_phone_2}
                        onChange={handleFooterContactChange}
                        placeholder="(+976) 7788-3482"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Утас 3:
                      </label>
                      <input
                        type="text"
                        name="mongolia_phone_3"
                        value={footerContacts.mongolia_phone_3}
                        onChange={handleFooterContactChange}
                        placeholder="(+976) 8888-3482"
                        className="custom-input"
                        disabled={footerLoading}
                      />
                    </div>
                  </div>
                </div>

                {/* ИМЭЙЛ ХАЯГ */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-200">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    📧 Имэйл хаяг
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Имэйл:
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={footerContacts.email}
                      onChange={handleFooterContactChange}
                      placeholder="jimguncargo@gmail.com"
                      className="custom-input"
                      disabled={footerLoading}
                    />
                  </div>
                </div>

                {/* SAVE BUTTON */}
                <div className="flex justify-end pt-4 border-t">
                  <button
                    type="submit"
                    className="custom-button flex items-center gap-2 px-8"
                    disabled={footerLoading}
                  >
                    <FaSave />
                    {footerLoading ? '⏳ Хадгалж байна...' : '💾 Хадгалах'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* CTA MODAL */}
      {showCtaModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="modal-header">
              <h3 className="text-xl font-bold">
                {editingCta ? 'CTA тохиргоо засах' : 'Шинэ CTA тохиргоо'}
              </h3>
              <button onClick={() => setShowCtaModal(false)} className="icon-button">
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Гарчиг <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ctaForm.section_title}
                    onChange={(e) => setCtaForm({ ...ctaForm, section_title: e.target.value })}
                    className="custom-input"
                    placeholder="Ачаа илгээх гэж байна уу?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Тайлбар
                  </label>
                  <textarea
                    value={ctaForm.section_description}
                    onChange={(e) => setCtaForm({ ...ctaForm, section_description: e.target.value })}
                    className="custom-input"
                    rows="3"
                    placeholder="Бидэнтэй холбогдоорой..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Утасны дугаарууд
                  </label>
                  
                  {/* Одоо байгаа дугаарууд */}
                  <div className="space-y-2 mb-3">
                    {ctaForm.phone_numbers.map((phone, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded">
                        <span>{phone.flag}</span>
                        <div className="flex-1">
                          <span className="font-semibold">{phone.label}:</span> {phone.number}
                        </div>
                        <button
                          onClick={() => removePhone(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Шинэ дугаар нэмэх */}
                  <div className="border-t pt-3">
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Flag (🇰🇷)"
                        value={newPhone.flag}
                        onChange={(e) => setNewPhone({ ...newPhone, flag: e.target.value })}
                        className="custom-input"
                      />
                      <input
                        type="text"
                        placeholder="Гарчиг"
                        value={newPhone.label}
                        onChange={(e) => setNewPhone({ ...newPhone, label: e.target.value })}
                        className="custom-input"
                      />
                      <input
                        type="text"
                        placeholder="Дугаар"
                        value={newPhone.number}
                        onChange={(e) => setNewPhone({ ...newPhone, number: e.target.value })}
                        className="custom-input col-span-2"
                      />
                    </div>
                    <button
                      onClick={addPhone}
                      className="custom-button-secondary w-full"
                    >
                      <FaPlus className="inline mr-2" />
                      Дугаар нэмэх
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowCtaModal(false)}
                className="custom-button-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleCtaSave}
                className="custom-button flex items-center gap-2"
              >
                <FaSave />
                Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT INFO MODAL */}
      {showContactModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="modal-header">
              <h3 className="text-xl font-bold">
                {editingContact ? 'Холбоо барих мэдээлэл засах' : 'Шинэ холбоо барих мэдээлэл'}
              </h3>
              <button onClick={() => setShowContactModal(false)} className="icon-button">
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Гарчиг <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contactForm.section_title}
                    onChange={(e) => setContactForm({ ...contactForm, section_title: e.target.value })}
                    className="custom-input"
                    placeholder="Солонгост холбоо барих"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Холбоо барих мэдээллүүд <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Одоо байгаа items */}
                  <div className="space-y-2 mb-3">
                    {contactForm.contact_items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded">
                        <div className="flex-1">{item}</div>
                        <button
                          onClick={() => removeContactItem(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Шинэ item нэмэх */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Жишээ: +82-10-5815-3005 эсвэл info@example.com"
                      value={newContactItem}
                      onChange={(e) => setNewContactItem(e.target.value)}
                      className="custom-input flex-1"
                    />
                    <button
                      onClick={addContactItem}
                      className="custom-button-secondary"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Дараалал
                    </label>
                    <input
                      type="number"
                      value={contactForm.display_order}
                      onChange={(e) => setContactForm({ ...contactForm, display_order: parseInt(e.target.value) })}
                      className="custom-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Идэвхтэй эсэх
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contactForm.is_active}
                        onChange={(e) => setContactForm({ ...contactForm, is_active: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span>{contactForm.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowContactModal(false)}
                className="custom-button-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleContactSave}
                className="custom-button flex items-center gap-2"
              >
                <FaSave />
                Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement;